<template>
  <input
    class="placeholder:italic bg-gray-600 block w-full border border-slate-300 rounded-md py-2 pl-3 pr-3 shadow-sm focus:outline-none focus:border-gray-100 focus:ring-sky-500 focus:ring-1 sm:text-sm"
    type="text"
    placeholder="YYYY-MM-DD HH:MM"
    @click="openCalendar()"
    @keyup.enter="closeCalendar(false)"
    @keydown.tab="closeCalendar(false)"
    @blur="closeCalendar(true)"
    @keyup="updateCalendar(selectedDate)"
    v-model="selectedDateTime"
  />
  <div v-if="!isValid(selectedDateTime)" class="text-xs text-red-500 text-left p-1">
    Invalid format detected. Valid date format is YYYY-MM-DD HH:MM.
  </div>
  <div v-if="showCalendar" class="absolute top-23 left-0 text-gray-800 bg-gray-200 shadow-lg border-1 px:width-40 p-2">
    <div class="grid grid-cols-4 gap-2 p-3">
      <div class="col-span-3">
        <div class="grid grid-cols-3 gap-2 p-3 font-bold text-center">
          <div @click="decreaseMonth()"><i class="fas fa-chevron-left" /></div>
          <div>{{ monthString }} {{ year }}</div>
          <div @click="increaseMonth()"><i class="fas fa-chevron-right" /></div>
        </div>
        <div class="grid grid-cols-7 gap-2 text-center text-gray-800 font-medium p-2">
          <div v-for="item in dayNames" :key="item">{{ item }}</div>
        </div>
        <div class="grid grid-cols-7 gap-2">
          <button :class="[{ 'text-gray-400': isDifferentMonth(item) }]" v-for="item in calendar" :key="item" @click="selectDate(item)">
            <div :class="dateSelected(item) ? 'bg-gray-800 rounded-5 text-white' : ''">
              {{ formatCalendarItem(item) }}
            </div>
          </button>
        </div>
      </div>
      <div class="col-span-1 overflow-auto">
        <div class="max-h-40 overflow=auto">
          <button
            v-for="hour in getHours()"
            :key="hour"
            class="px-2"
            :class="timeSelected(hour) ? 'bg-gray-800 text-white' : ''"
            @click="selectTime(hour)"
          >
            {{ hour }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeMount, ref, toRef, watch, onBeforeUnmount } from 'vue'

import { $date as useDate } from 'alga-js'

export default {
  name: 'DatePicker',
  props: {
    currentDate: {
      type: Date,
      required: false,
    },
  },
  emits: ['date-selected'],
  setup(props, { emit }) {
    // Calendar Display Controls
    const showCalendar = ref(false)
    const stayOpen = ref(false)

    function openCalendar() {
      showCalendar.value = true
    }

    function closeCalendar(wait) {
      if (wait) {
        setTimeout(() => {
          showCalendar.value = stayOpen.value
        }, 300)
      } else {
        showCalendar.value = false
      }
    }

    function keepCalendarOpen() {
      stayOpen.value = true
      setTimeout(() => {
        stayOpen.value = false
      }, 400)
    }

    // Calendar Data
    const year = ref(0)
    const month = ref(0)
    const selectedDate = ref('')
    const selectedTime = ref('')
    const selectedDateTime = ref('')
    const calendar = ref(null)

    function setCalendar() {
      const cal = useDate.calendar(year.value, month.value)
      cal.splice(0, 7)
      calendar.value = cal
    }

    function updateCalendar(item) {
      if ((item !== undefined) & (item !== '')) {
        if (/^\d{4}/.test(item)) {
          year.value = parseDate(item).year
          setCalendar()
        }
        if (/^\d{4}-([1-9]|0[1-9]|1[0-2])/.test(item)) {
          month.value = parseDate(item).month
          setCalendar()
        }
      }
    }

    onBeforeMount(() => {
      const setupDate = new Date()
      year.value = setupDate.getFullYear()
      month.value = Number(setupDate.getMonth()) + 1
      setCalendar()
      if ((props.currentDate !== undefined) & (props.currentDate !== '')) {
        setupFromProps()
      }
    })

    const propDate = toRef(props, 'currentDate')
    const unWatchDate = watch(propDate, (oldDate, newDate) => {
      if (newDate !== toDateObject(selectedDate.value, selectedTime.value)) {
        setupFromProps()
      }
    })

    function setupFromProps() {
      year.value = props.currentDate.getFullYear()
      month.value = props.currentDate.getMonth() + 1
      setCalendar()

      const hours = props.currentDate.getHours()

      selectedTime.value = `${hours <= 9 ? '0' + hours : hours}:00`
      selectedDate.value = props.currentDate.toISOString().split('T')[0]
      selectedDateTime.value = `${selectedDate.value} ${selectedTime.value}`
    }

    // Calendar View
    const monthString = computed(() => useDate.month(year.value, month.value + 1).slice(0, 3))
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    function getHours() {
      const formattedHours = []
      for (const i of Array(24).keys()) {
        if (i < 10) {
          formattedHours.push(`0${i}:00`)
        } else {
          formattedHours.push(`${i}:00`)
        }
      }
      return formattedHours
    }

    function formatCalendarItem(item) {
      return Number(parseDate(item).day)
    }

    function isDifferentMonth(item) {
      return parseDate(item).month !== month.value
    }

    function dateSelected(item) {
      return item === selectedDate.value
    }

    function timeSelected(item) {
      return item === selectedTime.value
    }

    // Calendar Actions
    function selectTime(item) {
      if (isValidTime(item)) {
        selectedTime.value = item
        if (isValidDate(selectedDate.value)) {
          setSelectedDateTime(selectedDate.value, selectedTime.value)
        }
      }
      closeCalendar()
    }

    function selectDate(item) {
      if (isValidDate(item)) {
        selectedDate.value = item
        selectedTime.value = isValidTime(selectedTime.value) ? selectedTime.value : '00:00'
        setSelectedDateTime(selectedDate.value, selectedTime.value)
      }
      closeCalendar()
    }

    function increaseMonth() {
      keepCalendarOpen()
      if (month.value === 12) {
        month.value = 1
        year.value = year.value + 1
      } else {
        month.value = month.value + 1
      }
      setCalendar()
    }

    function decreaseMonth() {
      keepCalendarOpen()
      if (month.value === 1) {
        month.value = 12
        year.value = year.value - 1
      } else {
        month.value = month.value - 1
      }
      setCalendar()
    }

    // Utilities
    function isValid(item) {
      if (item === undefined || item === '') {
        // Empty is acceptable
        return true
      }
      if (isValidDate(item)) {
        setSelectedDateTime(item, '00:00')
        return true
      }
      return /^\d{4}-(0[1-9]|1[0-2])-([1-9]|[0-2][0-9]|3[01]) ([0-1][0-9]|2[0-3]):(0[0-9]|[0-5][0-9])/.test(item)
    }

    function isValidDate(item) {
      return /^\d{4}-(0[1-9]|1[0-2])-([1-9]|0[1-9]|[12][0-9]|3[01])/.test(item) && item.length === 10
    }

    function isValidTime(item) {
      return /^([0-1][0-9]|2[0-3]):(0[0-9]|[0-5][0-9])/.test(item) && item.length === 5
    }

    function parseDate(dateString) {
      const p = dateString.split(' ')[0].split('-')
      return { year: p[0], month: Number(p[1]), day: p[2] }
    }

    function toDateObject(date, time) {
      const d = parseDate(date)
      const t = time.split(':')
      const dateObject = new Date(d.year, parseInt(d.month) - 1, d.day)
      dateObject.setHours(parseInt(t[0]), parseInt(t[1]), 0, 0)
      return dateObject
    }

    function setSelectedDateTime(date, time) {
      if (isValidDate(date) && isValidTime(time)) {
        selectedDateTime.value = `${date} ${time}`
        emit('date-selected', toDateObject(date, time))
      }
    }

    onBeforeUnmount(() => {
      unWatchDate()
    })

    return {
      showCalendar,
      openCalendar,
      closeCalendar,
      calendar,
      formatCalendarItem,
      isDifferentMonth,
      dateSelected,
      selectDate,
      selectTime,
      monthString,
      dayNames,
      year,
      increaseMonth,
      decreaseMonth,
      isValid,
      selectedDate,
      selectedDateTime,
      updateCalendar,
      getHours,
      timeSelected,
    }
  },
}
</script>

<style scoped>

</style>
